import React, { useState, useRef } from 'react';
import ReactAvatarEditor from 'react-avatar-editor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faSpinner, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';
import { logger } from '../../utils/logger';
import '../../styles/AvatarEditor.css';

interface AvatarEditorProps {
  initialImage?: string | null;
  onSave: (imageFile: Blob) => Promise<void>;
  onCancel: () => void;
}

/**
 * Component for editing and cropping profile avatar images in a circular shape
 */
const AvatarEditor: React.FC<AvatarEditorProps> = ({ initialImage, onSave, onCancel }) => {
  const [image, setImage] = useState<string | File | undefined>(initialImage || undefined);
  const [scale, setScale] = useState<number>(1);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const editorRef = useRef<ReactAvatarEditor | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validate file is an image
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPG, PNG, etc)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image is too large. Please select an image under 5MB.');
        return;
      }
      
      setImage(file);
      setError(null);
      logger.info('📸 Image selected for avatar');
    }
  };

  const handleSave = async () => {
    if (editorRef.current && image) {
      try {
        setSaving(true);
        setError(null);
        
        // Get the canvas with the cropped image
        const canvas = editorRef.current.getImageScaledToCanvas();
        
        // Convert canvas to blob
        const blob = await new Promise<Blob>((resolve) => {
          canvas.toBlob((blob: Blob | null) => {
            if (blob) {
              resolve(blob);
            }
          }, 'image/jpeg', 0.95);
        });
        
        // Send the blob to the parent component
        await onSave(blob);
        logger.success('✅ Avatar cropped and saved successfully');
      } catch (err) {
        logger.error('❌ Error saving avatar:', err);
        setError('Failed to save avatar. Please try again.');
      } finally {
        setSaving(false);
      }
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className="avatar-editor-container">
      <div className="avatar-editor-header">
        <h3>Edit Profile Picture</h3>
        <button 
          className="close-button" 
          onClick={onCancel} 
          disabled={saving}
          title="Cancel"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
      
      {error && <div className="avatar-editor-error">{error}</div>}
      
      <div className="avatar-editor-content">
        {image ? (
          <ReactAvatarEditor
            ref={editorRef}
            image={image}
            width={250}
            height={250}
            border={50}
            borderRadius={125}
            color={[26, 26, 26, 0.8]} // RGBA
            scale={scale}
            rotate={0}
            className="avatar-canvas"
          />
        ) : (
          <div className="avatar-placeholder" onClick={triggerFileInput}>
            <FontAwesomeIcon icon={faCamera} size="3x" />
            <p>Click to select an image</p>
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden-file-input"
          aria-label="Upload profile picture"
          title="Select a profile picture"
        />
        
        {image && (
          <div className="avatar-controls">
            <label htmlFor="zoom">Zoom:</label>
            <input
              id="zoom"
              type="range"
              min="1"
              max="2"
              step="0.01"
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              disabled={saving}
            />
            
            <div className="avatar-buttons">
              <button
                className="select-image-btn"
                onClick={triggerFileInput}
                disabled={saving}
              >
                Select New Image
              </button>
              <button
                className="save-avatar-btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCheck} /> Save
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarEditor;
